import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { formatCount } from "@admin-ui/utils/format-count";
import { Head, router } from "@inertiajs/react";
import { adminListingsPath, adminListingsReindexPath } from "@travily/routes";
import type AdminListingsIndex from "@travily/types/pages/AdminListingsIndex";
import { RefreshCcw01 } from "@untitledui/icons";
import { type ReactNode, useCallback } from "react";
import type { SortDescriptor } from "react-aria-components";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { IndexView, type ListingFilters } from "./index.view";

type AdminListingsIndexPageProps = AdminListingsIndex;

const FILTER_TO_RANSACK: Record<keyof ListingFilters, string> = {
  search: "title_or_tagline_cont",
  listable_type: "listable_type_eq",
  setup_status: "setup_status_eq",
};

function buildQuery({
  filters,
  sort,
  sortDir,
  page,
}: {
  filters: ListingFilters;
  sort: string | null;
  sortDir: string | null;
  page: number;
}) {
  const q: Record<string, string> = {};
  const keys: Array<keyof ListingFilters> = ["search", "listable_type", "setup_status"];
  for (const key of keys) {
    const value = filters[key];
    if (value !== "") q[FILTER_TO_RANSACK[key]] = value;
  }
  if (sort && sortDir) q.s = `${sort} ${sortDir}`;
  return { q, page };
}

function AdminListingsIndexPage({ listings, pagy, sort, sort_dir, filters }: AdminListingsIndexPageProps) {
  const account = useAdminLayoutAccount();

  const navigate = useCallback(
    (next: { filters?: ListingFilters; sort?: string | null; sortDir?: string | null; page?: number }) => {
      const query = buildQuery({
        filters: next.filters ?? filters,
        sort: next.sort !== undefined ? next.sort : sort,
        sortDir: next.sortDir !== undefined ? next.sortDir : sort_dir,
        page: next.page ?? 1,
      });
      router.get(adminListingsPath(), query, { preserveState: true, preserveScroll: true });
    },
    [filters, sort, sort_dir],
  );

  const handleSortChange = useCallback(
    (descriptor: SortDescriptor) => {
      const field = String(descriptor.column);
      const dir = descriptor.direction === "ascending" ? "asc" : "desc";
      navigate({ sort: field, sortDir: dir, page: 1 });
    },
    [navigate],
  );

  const handlePageChange = useCallback((nextPage: number) => navigate({ page: nextPage }), [navigate]);

  const handleSearchChange = useCallback(
    (value: string) => navigate({ filters: { ...filters, search: value }, page: 1 }),
    [filters, navigate],
  );

  const handleFilterChange = useCallback(
    (key: keyof ListingFilters, value: string) => navigate({ filters: { ...filters, [key]: value }, page: 1 }),
    [filters, navigate],
  );

  const handleReindex = useCallback(() => router.post(adminListingsReindexPath(), {}, { preserveScroll: true }), []);

  const listingCount = formatCount(pagy.count, "listing");

  return (
    <AdminLayout activeUrl={adminListingsPath()} account={account}>
      <Head title="Listings - Admin" />

      <AdminPageHeader
        breadcrumbs={[]}
        title="Listings"
        titleBadge={
          <Badge color="gray" size="sm">
            {listingCount}
          </Badge>
        }
        subtitle="Products and services across all accounts"
        actions={
          <Dropdown.Root>
            <Dropdown.DotsButton />
            <Dropdown.Popover className="w-min">
              <Dropdown.Menu
                onAction={(key) => {
                  if (key === "reindex") handleReindex();
                }}
              >
                <Dropdown.Item id="reindex" icon={RefreshCcw01} label="Reindex listings" />
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown.Root>
        }
      />

      <div className="mx-auto flex w-full max-w-container flex-col gap-6 px-4 lg:gap-8 lg:px-8">
        <IndexView
          listings={listings}
          pagy={pagy}
          sort={sort}
          sortDir={sort_dir}
          filters={filters}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />
      </div>
    </AdminLayout>
  );
}

AdminListingsIndexPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminListingsIndexPage;
